import {
  requiredSIPForTarget,
  requiredLumpsumForTarget,
  futureValueSIP,
  futureValueLumpsumPlusSIP,
  assetAllocation,
  hlvCoverNeeded,
  insuranceIRR,
  debtFundHoldSell,
} from "../engine/calculations";
import { formatINRCompact, formatPercent } from "./currency";
import { representativeAnnualIncome } from "./profileOptions";

// Task 16.2 — client-facing report builders. Each report is a small input form plus a build()
// that assembles PDF sections by REUSING existing engine outputs (no new engine maths). The
// backend /pdf/report endpoint owns branding, pagination, and the educational disclaimer; these
// builders only produce already-formatted display strings, exactly like the single-calculator PDF.

const item = (label, value) => ({ label, value });
const currency = (label, v) => ({ label, value: formatINRCompact(v) });

function memberGoals(profile) {
  if (!profile) return [];
  return [...(profile.short_term_goals || []), ...(profile.long_term_goals || [])];
}

// ---------------------------------------------------------------------------
// Report 1 — All-in-One Investment Proposal
// ---------------------------------------------------------------------------
const allInOne = {
  key: "report-all-in-one",
  title: "All-in-One Investment Proposal",
  description:
    "Pulls your saved goals, sizes the SIP/lumpsum each needs, suggests an asset allocation, checks your protection, and projects the whole plan.",
  fields: [
    { key: "annualIncome", label: "Annual income", type: "currency", default: 12_00_000, profileKey: "annualIncome" },
    { key: "age", label: "Your age", type: "number", default: 35 },
    {
      key: "riskProfile",
      label: "Risk profile",
      type: "select",
      default: "moderate",
      options: [
        { value: "conservative", label: "Conservative" },
        { value: "moderate", label: "Moderate" },
        { value: "aggressive", label: "Aggressive" },
      ],
    },
    { key: "supportYears", label: "Years of income to protect", type: "number", default: 15 },
    { key: "loans", label: "Outstanding loans", type: "currency", default: 20_00_000 },
    { key: "existingInvestments", label: "Existing investments", type: "currency", default: 10_00_000, profileKey: "netWorth" },
    { key: "existingCover", label: "Existing life cover", type: "currency", default: 0 },
  ],
  build(profile, assumptions, v) {
    const rate = assumptions.equityReturn;
    const currentYear = new Date().getFullYear();

    let goals = memberGoals(profile).map((g) => ({
      name: g.name,
      target: g.target_amount,
      year: g.target_year,
    }));
    let usingSample = false;
    if (goals.length === 0) {
      usingSample = true;
      goals = [{ name: "Sample goal (add goals in your profile)", target: 1_00_00_000, year: currentYear + 15 }];
    }

    const goalRows = goals.map((g) => {
      const years = Math.max(1, g.year - currentYear);
      const months = years * 12;
      const sip = requiredSIPForTarget(g.target, rate, months);
      const lumpsum = requiredLumpsumForTarget(g.target, rate, years);
      return { ...g, years, sip, lumpsum };
    });

    const totalTarget = goalRows.reduce((s, g) => s + g.target, 0);
    const totalMonthlySIP = goalRows.reduce((s, g) => s + g.sip, 0);
    const maxYears = Math.max(...goalRows.map((g) => g.years));

    const alloc = assetAllocation(v.riskProfile);
    const cover = hlvCoverNeeded({
      annualIncome: v.annualIncome,
      supportYears: v.supportYears,
      loans: v.loans,
      existingInvestments: v.existingInvestments,
      existingCover: v.existingCover,
    });

    const series = [];
    for (let y = 1; y <= maxYears; y++) {
      series.push({ year: y, value: futureValueSIP(totalMonthlySIP, rate, y * 12) });
    }

    const sections = [
      {
        title: "Goals Summary",
        intro: usingSample
          ? "You haven't saved any goals yet, so this uses one sample goal. Add goals in your profile for a personalised proposal."
          : "The goals from your profile, and the total corpus they add up to.",
        headline: item("Total corpus you're targeting", formatINRCompact(totalTarget)),
        items: goalRows.map((g) => currency(`${g.name} (${g.year})`, g.target)),
      },
      {
        title: "What each goal needs",
        intro: `Sized at an assumed ${formatPercent(rate)} annual return. Either invest the monthly SIP, or the one-time lumpsum today.`,
        table: {
          row_label: "Goal",
          col_label: "",
          row_headers: goalRows.map((g) => g.name),
          col_headers: ["Target", "Monthly SIP", "Lumpsum today"],
          cells: goalRows.map((g) => [formatINRCompact(g.target), formatINRCompact(g.sip), formatINRCompact(g.lumpsum)]),
        },
      },
      {
        title: "Recommended Asset Allocation",
        intro: `Based on a ${v.riskProfile} risk profile.`,
        items: [
          item("Equity", formatPercent(alloc.equity)),
          item("Debt", formatPercent(alloc.debt)),
          item("Gold", formatPercent(alloc.gold)),
        ],
      },
      {
        title: "Protection (Human Life Value)",
        headline: item("Life cover you should hold", formatINRCompact(cover)),
        items: [
          currency("Income to replace", v.annualIncome * v.supportYears),
          currency("Outstanding loans", v.loans),
          currency("Existing investments + cover", v.existingInvestments + v.existingCover),
        ],
        notes: cover > 0 ? [] : ["Your existing investments and cover already exceed the calculated need — good position to be in."],
      },
      {
        title: "Consolidated Projection",
        intro: `If you invest the combined ${formatINRCompact(totalMonthlySIP)}/month for all goals at ${formatPercent(rate)}, here's the trajectory.`,
        headline: item(`Projected corpus in ${maxYears} years`, formatINRCompact(futureValueSIP(totalMonthlySIP, rate, maxYears * 12))),
        series,
        notes: [
          "This is an educational projection, not a guarantee — actual returns vary year to year.",
          "Review the plan annually and after any major life change (new goal, income change, market move).",
        ],
      },
    ];

    return {
      report_title: allInOne.title,
      subtitle: "An educational projection based on your profile and inputs.",
      member_name: profile?.name || null,
      sections,
    };
  },
};

// ---------------------------------------------------------------------------
// Report 2 — Portfolio Projection Report
// ---------------------------------------------------------------------------
const portfolioProjection = {
  key: "report-portfolio-projection",
  title: "Portfolio Projection Report",
  description: "Project your current holdings plus ongoing SIP forward, with a year-by-year table and growth chart.",
  fields: [
    { key: "currentHoldings", label: "Current holdings (lumpsum)", type: "currency", default: 20_00_000, profileKey: "netWorth" },
    { key: "monthlySip", label: "Ongoing monthly SIP", type: "currency", default: 30_000 },
    { key: "returnRate", label: "Expected annual return", type: "percent", default: 12 },
    { key: "years", label: "Projection period (years)", type: "number", default: 20 },
  ],
  build(profile, assumptions, v) {
    const rate = v.returnRate / 100;
    const finalValue = futureValueLumpsumPlusSIP(v.currentHoldings, v.monthlySip, rate, v.years);
    const invested = v.currentHoldings + v.monthlySip * v.years * 12;

    const rows = [];
    const series = [];
    for (let y = 1; y <= v.years; y++) {
      const value = futureValueLumpsumPlusSIP(v.currentHoldings, v.monthlySip, rate, y);
      const yInvested = v.currentHoldings + v.monthlySip * y * 12;
      rows.push([String(y), formatINRCompact(yInvested), formatINRCompact(value)]);
      series.push({ year: y, invested: yInvested, value });
    }

    return {
      report_title: portfolioProjection.title,
      subtitle: "An educational projection of your current portfolio.",
      member_name: profile?.name || null,
      sections: [
        {
          title: "Your Inputs",
          items: [
            currency("Current holdings", v.currentHoldings),
            currency("Ongoing monthly SIP", v.monthlySip),
            item("Expected annual return", formatPercent(v.returnRate / 100)),
            item("Projection period", `${v.years} years`),
          ],
        },
        {
          title: "Projected Growth",
          headline: item(`Value in ${v.years} years`, formatINRCompact(finalValue)),
          items: [
            currency("Total invested", invested),
            currency("Wealth gained", finalValue - invested),
          ],
          series,
        },
        {
          title: "Year-by-year",
          table: {
            row_label: "Year",
            col_label: "",
            row_headers: rows.map((r) => r[0]),
            col_headers: ["Invested", "Value"],
            cells: rows.map((r) => [r[1], r[2]]),
          },
          notes: ["Projections assume a constant annual return; real markets are volatile year to year."],
        },
      ],
    };
  },
};

// ---------------------------------------------------------------------------
// Report 3 — Review of Existing Investment
// ---------------------------------------------------------------------------
const reviewExisting = {
  key: "report-review-existing",
  title: "Review of Existing Investment",
  description: "Assess a traditional policy's IRR and a debt fund's hold-or-sell decision, then get an educational summary.",
  fields: [
    { key: "policyAnnualPremium", label: "Traditional policy annual premium", type: "currency", default: 1_00_000 },
    { key: "policyTerm", label: "Policy term (years)", type: "number", default: 20 },
    { key: "policyMaturity", label: "Policy maturity value", type: "currency", default: 30_00_000 },
    { key: "debtCurrentValue", label: "Debt fund current value", type: "currency", default: 5_00_000 },
    { key: "debtCostBasis", label: "Debt fund cost basis", type: "currency", default: 4_00_000 },
    { key: "slabRate", label: "Your income tax slab rate", type: "percent", default: 30 },
    { key: "debtExtraYears", label: "Extra years you'd hold the debt fund", type: "number", default: 3 },
  ],
  build(profile, assumptions, v) {
    const equityRate = assumptions.equityReturn;
    const debtRate = assumptions.debtReturn;

    // Policy IRR: pay the premium each year 0..term-1, receive maturity at year `term`.
    const years = [];
    const premiums = [];
    const payouts = [];
    for (let y = 0; y <= v.policyTerm; y++) {
      years.push(y);
      premiums.push(y < v.policyTerm ? v.policyAnnualPremium : 0);
      payouts.push(y === v.policyTerm ? v.policyMaturity : 0);
    }
    const policyIrr = insuranceIRR(premiums, payouts, years);

    const debt = debtFundHoldSell(
      v.debtCurrentValue,
      v.debtCostBasis,
      "2023-06-01",
      v.slabRate,
      debtRate,
      v.debtExtraYears
    );
    const debtBetter = debt.advantage > 0 ? "Hold longer" : "Sell now";

    // SIP-vs-alternative: what the same premiums, invested annually in equity, could grow to —
    // an annuity-due FV of the premium stream (compare against the policy's own IRR).
    let sipAlternative = 0;
    for (let y = 0; y < v.policyTerm; y++) {
      sipAlternative = (sipAlternative + v.policyAnnualPremium) * (1 + equityRate);
    }

    const recommendations = [
      policyIrr < equityRate
        ? `The policy's IRR (${formatPercent(policyIrr)}) is below the assumed equity return (${formatPercent(equityRate)}). A term plan plus investing the difference often builds more wealth — but weigh the guaranteed nature and any tax benefits of the policy.`
        : `The policy's IRR (${formatPercent(policyIrr)}) is competitive with the assumed equity return — continuing may be reasonable.`,
      `On the debt fund, the numbers favour "${debtBetter}" under a ${formatPercent(v.slabRate / 100)} slab, but taxes and liquidity needs matter too.`,
      "This is educational analysis, not advice. Confirm any policy surrender or fund exit with a qualified adviser and a tax professional.",
    ];

    return {
      report_title: reviewExisting.title,
      subtitle: "An educational review of investments you already hold.",
      member_name: profile?.name || null,
      sections: [
        {
          title: "Traditional Policy — Internal Rate of Return (IRR)",
          intro: "The true annualised return your policy earns, accounting for every premium and the final payout.",
          headline: item("Policy IRR", formatPercent(policyIrr)),
          items: [
            currency("Annual premium", v.policyAnnualPremium),
            item("Term", `${v.policyTerm} years`),
            currency("Maturity value", v.policyMaturity),
            currency("Same premiums in equity could become", sipAlternative),
          ],
        },
        {
          title: "Debt Fund — Hold or Sell?",
          headline: item("Numbers favour", debtBetter),
          items: [
            currency("Sell now, reinvest net proceeds ends with", debt.sellNowResult),
            currency(`Hold ${v.debtExtraYears} more years ends with`, debt.holdMoreResult),
          ],
        },
        {
          title: "Recommendations",
          intro: "Educational framing only — not personalised advice.",
          notes: recommendations,
        },
      ],
    };
  },
};

export const REPORTS = [allInOne, portfolioProjection, reviewExisting];

export function getReportByKey(key) {
  return REPORTS.find((r) => r.key === key);
}
